import clsx from "clsx";
import {
  FocusEvent,
  KeyboardEvent,
  MouseEvent,
  RefObject,
  useEffect,
  useRef,
  useState,
} from "react";

import { selectInputOnFocus } from "#shared/helpers/selectInputOnFocus";
import { Flex } from "#shared/view/components/Flex";
import { Label } from "#shared/view/components/Label";
import { Stack } from "#shared/view/components/Stack";
import { minutesToTimeParts, timePartsToMinutes, TimeParts } from "shared";

import inputClasses from "./InputStyles.module.css";
import classes from "./TimeInput.module.css";

type TimeSegment = keyof TimeParts;

export type TimeInputProps = {
  value: number | null | undefined;
  onChange: (value: number) => void;
  onBlur?: (minutes: number) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  label?: string;
  required?: boolean;
  helper?: string;
  onClickWhy?: () => void;
  inlineLabel?: boolean;
  autoFocus?: boolean;
  small?: boolean;
  w?: string | number;
  min?: number;
  disabled?: boolean;
  "data-testid"?: string;
};

const SEGMENT_ORDER: TimeSegment[] = ["hours", "minutes", "seconds"];

const parseSegmentValue = (rawValue: string): number => {
  const parsed = Number.parseInt(rawValue, 10);

  return Number.isNaN(parsed) ? 0 : Math.max(0, parsed);
};

const clampSegmentValue = (segment: TimeSegment, value: number): number => {
  if (segment === "hours") {
    return Math.max(0, value);
  }

  return Math.min(59, Math.max(0, value));
};

const getInitialFocusSegment = (minutes: number | null | undefined): TimeSegment => {
  const totalMinutes = minutes ?? 0;

  if (totalMinutes < 1) {
    return "seconds";
  }

  if (totalMinutes < 60) {
    return "minutes";
  }

  return "hours";
};

export const TimeInput = ({
  value,
  onChange,
  onBlur,
  onKeyDown,
  label,
  required,
  helper,
  onClickWhy,
  inlineLabel = false,
  autoFocus = false,
  small = false,
  w = "100%",
  min = 0,
  disabled = false,
  "data-testid": dataTestId,
}: TimeInputProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const hoursRef = useRef<HTMLInputElement>(null);
  const minutesRef = useRef<HTMLInputElement>(null);
  const secondsRef = useRef<HTMLInputElement>(null);
  const segmentRefs: Record<TimeSegment, RefObject<HTMLInputElement | null>> = {
    hours: hoursRef,
    minutes: minutesRef,
    seconds: secondsRef,
  };

  const initialFocusSegmentRef = useRef(getInitialFocusSegment(value));
  const [parts, setParts] = useState<TimeParts>(() => minutesToTimeParts(value));
  const [drafts, setDrafts] = useState<Record<TimeSegment, string>>({
    hours: String(minutesToTimeParts(value).hours),
    minutes: String(minutesToTimeParts(value).minutes),
    seconds: String(minutesToTimeParts(value).seconds),
  });

  useEffect(() => {
    const nextParts = minutesToTimeParts(value);
    setParts(nextParts);
    setDrafts({
      hours: String(nextParts.hours),
      minutes: String(nextParts.minutes),
      seconds: String(nextParts.seconds),
    });
  }, [value]);

  useEffect(() => {
    if (!autoFocus) {
      return;
    }

    const focusRefMap = {
      hours: hoursRef,
      minutes: minutesRef,
      seconds: secondsRef,
    } as const;

    focusRefMap[initialFocusSegmentRef.current].current?.focus();
  }, [autoFocus]);

  const emitChange = (nextParts: TimeParts) => {
    const minutes = timePartsToMinutes(nextParts);
    const clampedMinutes = Math.max(min, minutes);

    onChange(clampedMinutes);
  };

  const commitSegment = (segment: TimeSegment, rawValue: string) => {
    const parsedValue = clampSegmentValue(segment, parseSegmentValue(rawValue));
    const nextParts = { ...parts, [segment]: parsedValue };

    setParts(nextParts);
    setDrafts((currentDrafts) => ({
      ...currentDrafts,
      [segment]: String(parsedValue),
    }));
    emitChange(nextParts);

    return nextParts;
  };

  const flushAllSegments = (): TimeParts => {
    const nextParts = SEGMENT_ORDER.reduce<TimeParts>(
      (acc, segment) => ({
        ...acc,
        [segment]: clampSegmentValue(segment, parseSegmentValue(drafts[segment])),
      }),
      parts,
    );

    setParts(nextParts);
    setDrafts({
      hours: String(nextParts.hours),
      minutes: String(nextParts.minutes),
      seconds: String(nextParts.seconds),
    });
    emitChange(nextParts);

    return nextParts;
  };

  const handleSegmentChange = (segment: TimeSegment, rawValue: string) => {
    if (!/^\d*$/.test(rawValue)) {
      return;
    }

    setDrafts((currentDrafts) => ({
      ...currentDrafts,
      [segment]: rawValue,
    }));
  };

  const handleSegmentBlur = (segment: TimeSegment) => {
    commitSegment(segment, drafts[segment]);
  };

  const handleContainerBlur = (event: FocusEvent<HTMLDivElement>) => {
    const focusMovedToAnotherSegment = containerRef.current?.contains(event.relatedTarget);

    if (focusMovedToAnotherSegment) {
      return;
    }

    const nextParts = flushAllSegments();
    onBlur?.(timePartsToMinutes(nextParts));
  };

  const focusSegment = (segment: TimeSegment) => {
    segmentRefs[segment].current?.focus();
  };

  const adjustSegment = (segment: TimeSegment, delta: number) => {
    const currentValue = parseSegmentValue(drafts[segment]);
    const nextValue = clampSegmentValue(segment, currentValue + delta);

    commitSegment(segment, String(nextValue));
  };

  const handleSegmentKeyDown = (segment: TimeSegment, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowUp") {
      event.preventDefault();
      adjustSegment(segment, 1);
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      adjustSegment(segment, -1);
      return;
    }

    if (event.key === "Tab" && !event.shiftKey) {
      const segmentIndex = SEGMENT_ORDER.indexOf(segment);

      if (segmentIndex < SEGMENT_ORDER.length - 1) {
        event.preventDefault();
        commitSegment(segment, drafts[segment]);
        focusSegment(SEGMENT_ORDER[segmentIndex + 1]!);
        return;
      }
    }

    if (event.key === "Tab" && event.shiftKey) {
      const segmentIndex = SEGMENT_ORDER.indexOf(segment);

      if (segmentIndex > 0) {
        event.preventDefault();
        commitSegment(segment, drafts[segment]);
        focusSegment(SEGMENT_ORDER[segmentIndex - 1]!);
        return;
      }
    }

    if (event.key === "Enter" || event.key === "Escape") {
      flushAllSegments();
    }

    onKeyDown?.(event);
  };

  const renderSegment = (segment: TimeSegment, className: string, ariaLabel: string) => (
    <input
      ref={segmentRefs[segment]}
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      className={className}
      style={segment === "hours" ? { width: `${Math.max(2, drafts.hours.length)}ch` } : undefined}
      value={drafts[segment]}
      disabled={disabled}
      aria-label={ariaLabel}
      data-testid={dataTestId ? `${dataTestId}-${segment}` : undefined}
      onFocus={selectInputOnFocus}
      onChange={(event) => handleSegmentChange(segment, event.target.value)}
      onBlur={() => handleSegmentBlur(segment)}
      onKeyDown={(event) => handleSegmentKeyDown(segment, event)}
    />
  );

  const handleContainerMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target instanceof HTMLInputElement) {
      return;
    }

    event.preventDefault();
  };

  const input = (
    <div
      ref={containerRef}
      className={clsx(
        small ? inputClasses.smallInput : inputClasses.input,
        classes.container,
        small && classes.containerSmall,
      )}
      style={{ width: w, minWidth: 0, maxWidth: "100%" }}
      data-testid={dataTestId}
      onMouseDown={handleContainerMouseDown}
      onBlur={handleContainerBlur}
    >
      {renderSegment("hours", clsx(classes.segment, classes.segmentHours), "hours")}
      <span className={classes.separator}>{small ? ":" : "h :"}</span>
      {renderSegment("minutes", clsx(classes.segment, classes.segmentMinutes), "minutes")}
      <span className={classes.separator}>{small ? ":" : "min :"}</span>
      {renderSegment("seconds", clsx(classes.segment, classes.segmentSeconds), "seconds")}
      {!small && <span className={classes.separator}>sec</span>}
    </div>
  );

  if (!label) {
    return input;
  }

  const Container = inlineLabel ? Flex : Stack;
  const containerProps = inlineLabel ? { gap: "xs" as const } : { gap: 0 };

  return (
    <Container {...containerProps}>
      <Label
        required={required}
        helper={helper}
        onClickWhy={onClickWhy}
        classNames={{
          label: inputClasses.label,
        }}
      >
        {label}
      </Label>
      {input}
    </Container>
  );
};
